import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Flex } from '@chakra-ui/react';
import { FiChevronDown } from 'react-icons/fi';
import { ff } from '../../../theme';
import LocationSearchModal from './LocationSearchModal';
import { getCurrentLocation } from '../../../utils/locationUtils';

// Minimal PlaceDetails type inline
interface PlaceDetails {
  lat: number;
  lng: number;
  fullAddress: string;
  streetNumber: string;
  streetName: string;
  city: string;
  state: string;
  country: string;
  zip: string;
}

interface LocationFilterProps {
  location: (PlaceDetails & { radius: number }) | null;
  searchRadius: number;
  onLocationChange: (location: (PlaceDetails & { radius: number }) | null, radius: number) => void;
}

const LocationFilter: React.FC<LocationFilterProps> = ({ location, searchRadius, onLocationChange }) => {
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<PlaceDetails | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Memoize the location change handler
  const handleLocationChange = useCallback((loc: PlaceDetails | null, radius: number) => {
    setSelectedLocation(loc);
    setIsLocationModalOpen(false);
    onLocationChange(loc ? { ...loc, radius } : null, radius);
  }, [onLocationChange]);

  // Update selected location when prop changes
  useEffect(() => {
    if (location) {
      setSelectedLocation(location);
    } else {
      setSelectedLocation(null);
    }
  }, [location]);

  const clearLocation = () => {
    setSelectedLocation(null);
    onLocationChange(null, searchRadius);
  };

  const place = location
    ? location.city || (location.fullAddress && location.fullAddress.length > 20
        ? `${location.fullAddress.substring(0, 20)}...`
        : location.fullAddress)
    : '';
  const label = place ? `${place} · ${location.radius} mi` : 'Location';

  return (
    <Flex align="center" gap="8px">
      <Button
        variant="unstyled"
        aria-label={`Change location, currently ${label}`}
        onClick={() => setIsLocationModalOpen(true)}
        isLoading={isGettingLocation}
        display="flex"
        alignItems="center"
        h="44px"
        minW="auto"
        px="0"
        color={ff.ink}
        fontSize="14px"
        fontWeight={600}
        lineHeight="normal"
        rightIcon={<FiChevronDown size={14} strokeWidth={2.4} aria-hidden />}
        iconSpacing="4px"
      >
        {label}
      </Button>

      <LocationSearchModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        onLocationSelect={handleLocationChange}
        currentLocation={selectedLocation}
        onClearLocation={clearLocation}
        radius={searchRadius}
      />
    </Flex>
  );
};

export default LocationFilter; 